// src/context/BannerContext.js
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import api from '../api';

// ✅ Local banner images (now includes sale)
import banner3day from '../assets/3daydelivery-banner.png';
import bannerRecent from '../assets/recent-banner.png';
import bannerSponsored from '../assets/sponsored-banner.png';
import bannerTrending from '../assets/trending-banner.png';
import bannerUnder20 from '../assets/under20-banner.png';
import bannerSale from '../assets/sale-banner.png'; // 🆕

const BannerCtx = createContext(null);

export function BannerProvider({ children }) {
  const [banners, setBanners] = useState([]);

  // Cache: key -> payload ({ products, hasMore, title, slug, ts })
  const [bannerCache, setBannerCache] = useState(() => new Map());
  // In-flight controllers: key -> AbortController
  const inFlight = useRef(new Map());

  // ⏱️ TTL for cached pages (tweak as needed)
  const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

  // Allowlist of slugs (mirror backend) — now includes 'sale'
  const ALLOWED = useMemo(
    () => new Set(['recent', 'trending', 'sponsored', 'three-day', 'under-20-pi', 'sale']),
    []
  );

  // Map slug -> local image
  const imageMap = useMemo(
    () => ({
      recent: bannerRecent,
      trending: bannerTrending,
      sponsored: bannerSponsored,
      'under-20-pi': bannerUnder20,
      'three-day': banner3day,
      sale: bannerSale, // 🆕
    }),
    []
  );

  const makeKey = useCallback((slug, page = 1, limit = 12, username = '') => {
    return `${slug}|p=${page}|l=${limit}|u=${username}`;
  }, []);

  // Fetch the list of banners (metadata)
  const loadBanners = useCallback(async () => {
    try {
      const res = await api.get('/banners');
      const serverBanners = Array.isArray(res.data?.banners) ? res.data.banners : [];

      const withImages = serverBanners
        .filter(b => ALLOWED.has(b.slug))
        .map(b => ({
          ...b,
          img: imageMap[b.slug] || b.img || null,
        }));

const fallback = [
  { slug: 'under-20-pi', title: 'Under 20 Pi',              subtitle: 'Affordable finds',         img: imageMap['under-20-pi'] },
  { slug: 'recent',      title: 'Discover Recent Products', subtitle: 'Freshly added',            img: imageMap.recent },
  { slug: 'trending',    title: 'Trending Now',             subtitle: 'Hot this week',            img: imageMap.trending },
  { slug: 'sponsored',   title: 'Sponsored Picks',          subtitle: 'Featured by sellers',      img: imageMap.sponsored },
  { slug: 'three-day',   title: '3-Day Delivery',           subtitle: 'Fast shipping',            img: imageMap['three-day'] },
  { slug: 'sale',        title: 'On Sale Now',              subtitle: 'Limited-time Pi discounts',img: imageMap.sale },
];



      setBanners(withImages.length ? withImages : fallback);
    } catch (err) {
      console.error('❌ Failed to load banners:', err);
      setBanners([
        { slug: 'recent',      title: 'Discover Recent Products', subtitle: 'Freshly added',            img: imageMap.recent },
        { slug: 'trending',    title: 'Trending Now',             subtitle: 'Hot this week',            img: imageMap.trending },
        { slug: 'sponsored',   title: 'Sponsored Picks',          subtitle: 'Featured by sellers',      img: imageMap.sponsored },
        { slug: 'three-day',   title: '3-Day Delivery',           subtitle: 'Fast shipping',            img: imageMap['three-day'] },
        { slug: 'under-20-pi', title: 'Under 20 Pi',              subtitle: 'Affordable finds',         img: imageMap['under-20-pi'] },
        { slug: 'sale',        title: 'On Sale Now',              subtitle: 'Limited-time Pi discounts',img: imageMap.sale }, // 🆕
      ]);
    }
  }, [ALLOWED, imageMap]);

  // Fetch products for a banner page, with cache+TTL and optional force refresh
  const fetchBannerProducts = useCallback(
    async (slug, page = 1, limit = 12, username, opts = {}) => {
      const { force = false } = opts;
      const key = makeKey(slug, page, limit, username || '');

      // 1) Serve from cache if fresh (unless force)
      const cached = bannerCache.get(key);
      if (cached && !force) {
        const fresh = cached.ts && (Date.now() - cached.ts) < CACHE_TTL_MS;
        if (fresh) return cached;
      }

      // 2) Abort any existing request for this key
      const prev = inFlight.current.get(key);
      if (prev) {
        try { prev.abort(); } catch {}
        inFlight.current.delete(key);
      }

      // 3) Fire request
      const ctrl = new AbortController();
      inFlight.current.set(key, ctrl);

      try {
        const { data } = await api.get(`/banners/${slug}/products`, {
          params: { page, limit, username },
          signal: ctrl.signal,
        });

        const payload = {
          products: data?.products || [],
          hasMore: !!data?.hasMore,
          title: data?.title || 'Results',
          slug: data?.slug || slug,
          ts: Date.now(),
        };

        setBannerCache(prevMap => {
          const next = new Map(prevMap);
          next.set(key, payload);
          return next;
        });

        return payload;
      } catch (err) {
        if (err?.name === 'AbortError') {
          // Aborted fetch due to a newer one; return null to caller
          return null;
        }
        console.error(`❌ Failed to load products for banner ${slug}:`, err);
        const fallback = { title: '', products: [], hasMore: false, slug, ts: Date.now() };
        setBannerCache(prevMap => {
          const next = new Map(prevMap);
          next.set(key, fallback);
          return next;
        });
        return fallback;
      } finally {
        const cur = inFlight.current.get(key);
        if (cur === ctrl) inFlight.current.delete(key);
      }
    },
    [bannerCache, makeKey]
  );

  // Clear all cached banner pages (used by Marketplace reload)
  const clearBannerCache = useCallback(() => setBannerCache(new Map()), []);

  // Optionally invalidate only one slug's pages
  const invalidateSlug = useCallback((slug) => {
    setBannerCache(prev => {
      const next = new Map(prev);
      for (const k of next.keys()) {
        if (k.startsWith(`${slug}|`)) next.delete(k);
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      banners,
      loadBanners,
      fetchBannerProducts,
      bannerCache,      // expose Map so consumers can .get(key)
      makeKey,
      clearBannerCache,
      invalidateSlug,
    }),
    [banners, loadBanners, fetchBannerProducts, bannerCache, makeKey, clearBannerCache, invalidateSlug]
  );

  return <BannerCtx.Provider value={value}>{children}</BannerCtx.Provider>;
}

export function useBanners() {
  return useContext(BannerCtx);
}
